import { useCallback, useMemo, useRef } from "react";
import {
  CreatableFieldDocumentType,
  CreatableFieldsQuery,
  useCreatableFieldsQuery,
} from "generated/general";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import { TEMPERATURE_TYPE_CREATABLE_FIELD_NAME } from "utils/Logistic/temperatureType";

export interface TemperatureTypeOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export const useTemperatureTypeOptions = () => {
  const graphQLClient = createGraphQLClientWithMiddleware("general");

  const { data, isSuccess, isLoading } =
    useCreatableFieldsQuery<CreatableFieldsQuery>(graphQLClient, {
      findManyInput: {
        document_type: CreatableFieldDocumentType.DeliveryTrip,
        creatable_fields_name: TEMPERATURE_TYPE_CREATABLE_FIELD_NAME,
      },
    });

  const options: TemperatureTypeOption[] = useMemo(() => {
    const rows = data?.CreatableFields ?? [];
    return rows.map((row) => ({
      label: row.name,
      value: row.name,
      disabled: false,
    }));
  }, [data]);

  return { options, isSuccess, isLoading };
};

/**
 * Lazy fetcher for ag-grid Set Filter `values: async (params) => {}`. Master
 * rows are read from CreatableField at filter-open time so the list reflects
 * any user-added temperature types. Mirrors `useVehicleTypeValues`.
 */
export const useTemperatureTypeValues = () => {
  // Memoize the GraphQL client so refetch / fetchValues stay stable across
  // renders — otherwise ag-grid columnDefs useEffects that depend on them
  // fire every render and can blow the update depth.
  const graphQLClient = useMemo(
    () => createGraphQLClientWithMiddleware("general"),
    [],
  );

  const { refetch } = useCreatableFieldsQuery<CreatableFieldsQuery>(
    graphQLClient,
    {
      findManyInput: {
        document_type: CreatableFieldDocumentType.DeliveryTrip,
        creatable_fields_name: TEMPERATURE_TYPE_CREATABLE_FIELD_NAME,
      },
    },
    { enabled: false },
  );

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const fetchValues = useCallback(async () => {
    const { data } = await refetchRef.current();
    const rows = data?.CreatableFields ?? [];
    return rows.map((row) => row.name);
  }, []);

  return { fetchValues };
};
